'use client';
import cn from 'classnames';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Suspense } from 'react'

import { NewsletterSubscribeForm } from './NewsletterSubscribeForm';
import SignToNewsletter from './SignToNewsletter';

import { AnalyticsParams } from '@components/Analytics/Google/GAScript';
import { Button } from '@components/Form';
import Loader from '@components/root/Loader';
import StarSvg from 'public/static/premium/star-hollow.svg';
import LoginForm from 'src/app/[locale]/(auth)/login/components/LoginForm';

interface NewsBlockedBannerProps {
  className?: string;
  initialState?: BannerView;
  analyticsData?: AnalyticsParams;
}

const LoginComponent = ({ userExists, onReturn }: { userExists: boolean; onReturn: () => void }) => {
  const t = useTranslations('auth.registerV2');
  return (
    <Suspense>
      <LoginForm
        userExists={userExists}
        otherActions={[
          {
            label: t('return'),
            onClick: onReturn,
          },
        ]}
      />
    </Suspense>
  );
};

const NewsletterComponent = () => {
  return <SignToNewsletter />;
};

export type BannerView =
  | 'LoggedOutForm'
  | 'ShowLoginForm'
  | 'ExistsActiveUser'
  | 'ExistsInactiveUser'
  | 'NewsletterForm'
  | 'Loader';

export default function NewsBlockedBanner({ initialState, className, analyticsData }: NewsBlockedBannerProps) {
  const [state, setState] = useState(initialState);
  const t = useTranslations('auth.registerV2');

  const onUserCheck = (isActive: boolean) => {
    if (isActive) {
      setState('ExistsActiveUser');
    } else {
      setState('ExistsInactiveUser');
    }
  };

  useEffect(() => {
    setState(initialState);
  }, [initialState]);

  return (
    <div
      className={cn(
        'w-full rounded-md  bg-white px-8 py-6 text-xs font-light text-premium-dark drop-shadow-md lg:px-12 lg:py-8 lg:text-base dark:bg-premium-dark dark:text-white',
        className,
      )}
    >
      {state === 'Loader' && <Loader />}
      {state !== 'Loader' && (
        <div className="flex flex-col items-center justify-center gap-4">
          <StarSvg />
        </div>
      )}
      {state === 'LoggedOutForm' && (
        <NewsletterSubscribeForm
          title={t('banner.title')}
          subtitle={t('banner.subtitle')}
          actionLabel={t('banner.actionLabel')}
          isLoggedIn={false}
          setIsLoginFormTriggered={() => setState('ShowLoginForm')}
          onUserCheck={onUserCheck}
          analyticsData={analyticsData}
        />
      )}
      {state === 'ExistsActiveUser' && <LoginComponent userExists={true} onReturn={() => setState('LoggedOutForm')} />}
      {state === 'ShowLoginForm' && <LoginComponent userExists={false} onReturn={() => setState('LoggedOutForm')} />}
      {state === 'ExistsInactiveUser' && (
        <>
          <h3 className="mb-1 text-center text-lg font-bold lg:text-2xl">{t('banner.inactiveUser.title')}</h3>
          <p className="mb-4 text-center">{t('banner.inactiveUser.subtitle')}</p>
          <div className="mb-4 flex w-full flex-row items-center justify-center lg:mb-8">
            <Button onClick={() => setState('LoggedOutForm')}>{t('return')}</Button>
          </div>
        </>
      )}
      {state === 'NewsletterForm' && <NewsletterComponent />}
    </div>
  );
}
